/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import { ComponentChild, createContext } from 'preact';

// Core
import { Navigation } from '@app';
import { Button, Popover, RangeSlider, SegmentedControl } from '@client/components';
import { Props as TBtnProps } from '@client/components/Button'
import useContext, { ClientContext } from '@/client/context';

// App
import { buildSearchUrl, getCurrentFilters } from '@/common/libs/filters';
import DomainFiltersForm from '@/common/forms/domains/filters';
import sectorIcons from './sectorIcons';
import { useMemo } from 'preact/hooks';

type TFilter = {
    label: string,
    fields: string[],
    width?: number,
    choices: () => React.ReactNode | Promise<React.ReactNode>,
    toText: () => ComponentChild,
    setOpen?: (open: boolean) => void,
}

export type TFilters = ReturnType<typeof createSearchFilters>

// Create a context with a default value (which may be overridden by the provider)
const FiltersContext = createContext<TFilters | undefined>(undefined);

const defaultFilters = {
    status: ['available', 'resell', 'expired'],
}

// This provider creates the filters object once (using useMemo) and passes it via context.
export const FiltersProvider: React.FC = ({ children }) => {

    const filters = useMemo<TFilters>(
        () => createSearchFilters(),
        [] // empty dependency array to ensure it's created only once
    );

    return (
        <FiltersContext.Provider value={filters}>
            {children}
        </FiltersContext.Provider>
    );
};

export function useSearchFilters(): TFilters {

    const { app, Router, context } = useContext();

    const [filters, setFilters] = React.useState({
        current: { ...defaultFilters },
        list: [],
    });

    // On page url change, update filters
    React.useEffect(() => {
        return Router.on('page.changed', (url: string) => {
    
            const updatedFilters = createSearchFilters(context);
            setFilters(updatedFilters);
    
        });
    }, []);
    
    return filters;
}

const createSearchFilters = (contextInit?: ClientContext) => {

    const { app, context, api } = contextInit || useContext();

    const currentFilters = getCurrentFilters(context);

    const FilterChangeButton = ({ filter, value, ...btnProps }: {
        filter: string,
        value: string,
    } & TBtnProps) => {
        return (
            <li>
                <Button
                    icon={btnProps.color ? (
                        <span class={"pastille bg " + btnProps.color} />
                    ) : undefined}
                    active={currentFilters[filter] === value}
                    link={buildSearchUrl({ [filter]: value, page: 1 }, app.Router, context)}
                    children={btnProps.title}
                    {...btnProps}
                    target={undefined}
                    class="col-1"
                />
            </li>
        )
    }

    // TODO:; improve access-
    const typeChoices = DomainFiltersForm.fields.type[1][0];
    const statusChoices = DomainFiltersForm.fields.status[1][0];
    const formatChoices = DomainFiltersForm.fields.format[1][0];
    const sortChoices = DomainFiltersForm.fields.sort[1][0];

    const missionFilters: TFilter[] = [{
        label: 'Status',
        fields: ['status'],
        choices: () => statusChoices.map(choice => (
            <FilterChangeButton filter="status" color={choice.color}
                value={choice.value} title={choice.label} />
        )),
        toText: () => {
            if (currentFilters.status)
                return currentFilters.status
        } 
    }, {
        label: 'Format',
        fields: ['format'],
        choices: () => formatChoices.map(choice => (
            <FilterChangeButton filter="format" color={choice.color}
                value={choice.value} title={choice.label} />
        )),
        toText: () => {
            if (currentFilters.format)
                return currentFilters.format
        } 
    
    // domains.purchasePrice removed, prices are checked on the fly
    /*}, {
        label: 'Price',
        fields: ['minPrice', 'maxPrice'],
        width: 20,
        choices: () => <>
            <li class="pd-1">
                Filter domains by the price it's sold on common registrars. Note: these prices are indicative and may vary a bit.
            </li>
            <li class="pd-1">
                <RangeSlider
                    minRange={100} min={10} max={1000}
                    step={1} labelAlwaysOn
                    defaultValue={[
                        currentFilters.minPrice || 10,
                        currentFilters.maxPrice || 1000
                    ]}
                    label={(value) => `${value === 1000 ? '1000+' : value}$`}
                    onChangeEnd={([min, max]) => app.Router.go(buildSearchUrl({
                        minPrice: min, maxPrice: max
                    }, app.Router, context))}
                />
            </li>
        </>,
        toText: () => {
            if (currentFilters.minPrice && currentFilters.maxPrice)
                return `From ${currentFilters.minPrice}$ to ${currentFilters.maxPrice}$`;
            else if (currentFilters.minPrice)
                return `Minimum ${currentFilters.minPrice}$`;
            else if (currentFilters.maxPrice)
                return `Maximum ${currentFilters.maxPrice}$`;
        }*/

    }, {
        label: 'Length',
        fields: ['minLength', 'maxLength'],
        width: 20,
        choices: () => <>
            <li class="pd-1">
                Filter domains by the number of characters (excluding the TLD).
            </li>
            <li class="pd-1">
                <RangeSlider
                    minRange={1} min={1} max={20}
                    step={1} labelAlwaysOn
                    defaultValue={[
                        currentFilters.minLength || 1,
                        currentFilters.maxLength || 20
                    ]}
                    label={(value) => `${value === 20 ? '20+' : value}`}
                    onChangeEnd={([min, max]) => app.Router.go(buildSearchUrl({
                        minLength: min, maxLength: max
                    }, app.Router, context))}
                />
            </li>
        </>,
        toText: () => {
            if (currentFilters.minLength && currentFilters.maxLength)
                return `From ${currentFilters.minLength} to ${currentFilters.maxLength} characters`;
            else if (currentFilters.minLength)
                return `Minimum ${currentFilters.minLength} characters`;
            else if (currentFilters.maxLength)
                return `Maximum ${currentFilters.maxLength} characters`;
        }
    }, {
        label: 'Extensions',
        fields: ['tld'],
        choices: () => Navigation.tlds().then(tlds => tlds.map(tld => (
            <FilterChangeButton filter="tld" value={tld} title={'.' + tld} />
        ))),
        toText: () => {
            if (currentFilters.tld)
                return `.${currentFilters.tld}`;
        }
    }, {
        label: 'Sector',
        fields: ['sector'],
        choices: () => Navigation.sectors().then(sectors => sectors.map(sector => (
            <FilterChangeButton filter="sector"
                icon={sectorIcons[sector.slug]}
                value={sector.slug} title={sector.name} />
        ))),
        toText: () => {
            if (currentFilters.sector)
                return currentFilters.sector;
        }
    }, {
        label: 'Sentiment',
        fields: ['minSentiment', 'maxSentiment'],
        width: 20,
        choices: () => <>
            <li class="pd-1">
                The sentiment score describes how positive or negative the domain is. 0 is negative, 50 is neutral, 100 is positive.
            </li>
            <li class="pd-1">
                <RangeSlider
                    minRange={1} min={1} max={100}
                    step={1} labelAlwaysOn
                    defaultValue={[
                        currentFilters.minSentiment || 0,
                        currentFilters.maxSentiment || 100
                    ]}
                    label={(value) => `${value}%`}
                    onChangeEnd={([min, max]) => app.Router.go(buildSearchUrl({
                        minSentiment: min, maxSentiment: max
                    }, app.Router, context))}
                />
            </li>
        </>,
        toText: () => {
            if (currentFilters.minSentiment && currentFilters.maxSentiment)
                return `Sentiment ${currentFilters.minSentiment}% - ${currentFilters.maxSentiment}%`;
            else if (currentFilters.minSentiment)
                return `Sentiment > ${currentFilters.minSentiment}%`;
            else if (currentFilters.maxSentiment)
                return `Sentiment < ${currentFilters.maxSentiment}%`;
        }
    }, {
        label: 'Popularity',
        fields: ['minPopularity', 'maxPopularity'],
        width: 20,
        choices: () => <>
            <li class="pd-1">
                The popularity score describes how popular the word composing the domain is. 0 is a unusual word, and 100 is word used in daily life.
            </li>
            <li class="pd-1">
                <RangeSlider
                    minRange={1} min={1} max={100}
                    step={1} labelAlwaysOn
                    defaultValue={[
                        currentFilters.minPopularity || 0,
                        currentFilters.maxPopularity || 100
                    ]}
                    label={(value) => `${value}%`}
                    onChangeEnd={([min, max]) => app.Router.go(buildSearchUrl({
                        minPopularity: min, maxPopularity: max
                    }, app.Router, context))}
                />
            </li>
        </>,
        toText: () => {
            if (currentFilters.minPopularity && currentFilters.maxPopularity)
                return `Popularity ${currentFilters.minPopularity}% - ${currentFilters.maxPopularity}%`;
            else if (currentFilters.minPopularity)
                return `Popularity > ${currentFilters.minPopularity}%`;
            else if (currentFilters.maxPopularity)
                return `Popularity < ${currentFilters.maxPopularity}%`;
        }
    }, {
        label: 'Brandability',
        fields: ['minBrandability', 'maxBrandability'],
        width: 20,
        choices: () => <>
            <li class="pd-1">
                The brandability score describes how brandable the domain is. 0 is not brandable, and 100 is very brandable.
            </li>
            <li class="pd-1">
                <RangeSlider
                    minRange={1} min={1} max={100}
                    step={1} labelAlwaysOn
                    defaultValue={[
                        currentFilters.minBrandability || 0,
                        currentFilters.maxBrandability || 100
                    ]}
                    label={(value) => `${value}%`}
                    onChangeEnd={([min, max]) => app.Router.go(buildSearchUrl({
                        minBrandability: min, maxBrandability: max
                    }, app.Router, context))}
                />
            </li>
        </>,
        toText: () => {
            if (currentFilters.minBrandability && currentFilters.maxBrandability)
                return `Brandability ${currentFilters.minBrandability}% - ${currentFilters.maxBrandability}%`;
            else if (currentFilters.minBrandability)
                return `Brandability > ${currentFilters.minBrandability}%`;
            else if (currentFilters.maxBrandability)
                return `Brandability < ${currentFilters.maxBrandability}%`;
        }
    }, {
        label: 'Demand',
        fields: ['minDemand', 'maxDemand'],
        width: 20,
        choices: () => <>
            <li class="pd-1">
                The demand score evaluates the buying pressure on the domain. 0 is no demand, and 100 is high probability of being sold soon.
            </li>
            <li class="pd-1">
                <RangeSlider
                    minRange={1} min={1} max={100}
                    step={1} labelAlwaysOn
                    defaultValue={[
                        currentFilters.minDemand || 0,
                        currentFilters.maxDemand || 100
                    ]}
                    label={(value) => `${value}%`}
                    onChangeEnd={([min, max]) => app.Router.go(buildSearchUrl({
                        minDemand: min, maxDemand: max
                    }, app.Router, context))}
                />
            </li>
        </>,
        toText: () => {
            if (currentFilters.minDemand && currentFilters.maxDemand)
                return `Demand ${currentFilters.minDemand}% - ${currentFilters.maxDemand}%`;
            else if (currentFilters.minDemand)
                return `Demand > ${currentFilters.minDemand}%`;
            else if (currentFilters.maxDemand)
                return `Demand < ${currentFilters.maxDemand}%`;
        }
    }, {
        label: 'Word Type',
        fields: ['type'],
        choices: () => typeChoices.map(choice => (
            <FilterChangeButton filter="type" value={choice.value} title={choice.label} />
        )),
        toText: () => {
            if (currentFilters.type)
                return currentFilters.type;
        }
    }, {
        label: 'Sort by',
        fields: ['sort'],
        choices: () => sortChoices.map(choice => (
            <FilterChangeButton filter="sort" value={choice.value} title={choice.label} />
        )),
        toText: () => {
            if (currentFilters.sort)
                return currentFilters.sort;
        }
    }]

    return {
        current: currentFilters,
        list: missionFilters,
    }
}

const FilterButton = ({ filter, filterIndex, filtersValues }: {
    filter: TFilter,
    filtersValues: TFilters,
    filterIndex: number,
}) => {

    const context = useContext();

    const hasFilter = filter.fields.some(field => filtersValues.current[field]);
    const emptyFilters = { page: 1 };
    for (const field of filter.fields)
        emptyFilters[field] = undefined;

    const [choices, setChoices] = React.useState<React.ReactNode[]>(null);

    const openState = React.useState(false);
    const [open, setOpen] = openState;
    filter.setOpen = setOpen;

    // Load choices when filter is opened
    React.useEffect(() => {

        const shouldLoad = open && !choices;
        if (!shouldLoad) return;

        const choicesList = filter.choices();
        if (choicesList instanceof Promise)
            choicesList.then(setChoices);
        else
            setChoices(choicesList);

    }, [open]); 

    return (
        <Popover state={openState} width={filter.width} tag="li" content={(
            <ul class="bg white col menu">
                {choices === null ? (
                    <div class="pdv-3 col al-center">
                        <i src="spin" />
                    </div>
                ) : <> 
                    {hasFilter && (
                        <li>
                            <Button icon="times"
                                link={buildSearchUrl(emptyFilters, context.Router, context)}>
                                Clear Filter
                            </Button>
                        </li>
                    )}
                    {choices}
                </>}
            </ul>
        )}>
            <Button iconR="angle-down" icon={hasFilter && (
                <span class={"pastille bg color" + (filterIndex + 1)} />
            )}>
                {hasFilter ? (
                    <strong>{filter.label}</strong>
                ) : filter.label}
            </Button>
        </Popover>
    )
}

/*----------------------------------
- COMPONENT
----------------------------------*/

export default () => {

    /*----------------------------------
    - INIT
    ----------------------------------*/

    const { api, app, context, page, toast, data } = useContext();

    const filters = useSearchFilters();

    /*----------------------------------
    - RENDER
    ----------------------------------*/
    return (
        <nav class="col">
            <ul class="row menu al-left scrollable pdh-1 pdb-1">

                {filters.list.map((filter, filterIndex) => (
                    <FilterButton filter={filter} filterIndex={filterIndex} filtersValues={filters} />
                ))}
            </ul>
        </nav>
    )
}